import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { supabase } from "@/lib/supabase";

/**
 * Operating settings data layer (migration 0213).
 *
 * Reads and writes go straight to the tables under RLS, the same shape the
 * catalog uses (0004 services_admin): a settings edit carries no cross-table
 * business rule, so no Edge Function is required (API Conventions § route
 * types). The database is the gate — `distance_surcharge_bands` is world-
 * readable because the client previews a surcharge before a booking exists,
 * `operating_settings` is staff-read, and BOTH are superadmin-write.
 *
 * Rows are parsed by Zod on the way out: Postgres `numeric` can arrive over
 * PostgREST as a string, and the screen does arithmetic on these values.
 *
 * EVERY VALUE HERE IS IN EFFECT, for new work only. The booking server and the
 * client preview price from `distance_surcharge_bands` (#198); the rider-sessions
 * function pays from the rider rate and staffs from the riders-per-therapist
 * ratio, and `therapist_rank_minimum()` reads the securing minimums (#207, 0217).
 */

/** Postgres `numeric` arrives as a string over PostgREST; coerce both shapes. */
const numeric = z.coerce.number();

const bandSchema = z.object({
  id: z.string().uuid(),
  min_km: numeric,
  max_km: numeric,
  client_rate: numeric,
  rider_rate: numeric,
  therapist_rate: numeric,
  min_price_solo_php: numeric,
  min_price_per_client_php: numeric,
});

export const RANKS = ["bronze", "silver", "gold", "diamond"] as const;
export type Rank = (typeof RANKS)[number];

const operatingSettingsSchema = z.object({
  id: numeric,
  service_area_limit_km: numeric,
  distance_override_min_km: numeric,
  rider_base_fare_php: numeric,
  rider_base_covers_km: numeric,
  rider_per_km_php: numeric,
  min_secured_bronze: numeric,
  min_secured_silver: numeric,
  min_secured_gold: numeric,
  min_secured_diamond: numeric,
  therapists_per_rider: numeric,
  booking_start_hour: numeric,
  // 0301 — the Confirm Pick-Up range, metres.
  pickup_range_m: numeric,
  // 0310 — the two cancellation fee amounts, pesos.
  cancellation_fee_before_pickup_php: numeric,
  cancellation_fee_after_pickup_php: numeric,
  // 0313 — how the after-pickup fee is split among the staff on the job.
  // 0334 — `percentage` is retired; `amount` is a fixed peso amount per eligible person.
  cancellation_share_mode: z.enum(["equal", "amount"]),
  cancellation_share_therapist_php: numeric.nullable(),
  cancellation_share_rider_php: numeric.nullable(),
  cancellation_share_therapist_ranks: z.array(z.enum(RANKS)),
  cancellation_share_rider_ranks: z.array(z.enum(RANKS)),
  // 0332 — hours before the regular opening a rank may start securing; null = off.
  early_securing_hours_bronze: numeric.nullable(),
  early_securing_hours_silver: numeric.nullable(),
  early_securing_hours_gold: numeric.nullable(),
  early_securing_hours_diamond: numeric.nullable(),
  // 0337 — days an old Therapist Code keeps resolving after it is replaced.
  retired_therapist_code_grace_days: numeric,
  // 0353/0361 — cash hand-over distance check: person to person, and the hub leg.
  cash_handover_proximity_enabled: z.boolean(),
  cash_handover_range_m: numeric,
  hub_cash_proximity_enabled: z.boolean(),
  hub_cash_range_m: numeric,
  // 0364 — her Return pick-up waits for the cash to be in the rider's hand.
  cash_before_pickup_required: z.boolean(),
  updated_at: z.string(),
});

export type DistanceBand = z.infer<typeof bandSchema>;
export type OperatingSettings = z.infer<typeof operatingSettingsSchema>;

const BAND_COLUMNS =
  "id, min_km, max_km, client_rate, rider_rate, therapist_rate, min_price_solo_php, min_price_per_client_php";

const SETTINGS_COLUMNS =
  "id, service_area_limit_km, distance_override_min_km, rider_base_fare_php, rider_base_covers_km, rider_per_km_php, min_secured_bronze, min_secured_silver, min_secured_gold, min_secured_diamond, therapists_per_rider, booking_start_hour, pickup_range_m, cancellation_fee_before_pickup_php, cancellation_fee_after_pickup_php, cancellation_share_mode, cancellation_share_therapist_php, cancellation_share_rider_php, cancellation_share_therapist_ranks, cancellation_share_rider_ranks, early_securing_hours_bronze, early_securing_hours_silver, early_securing_hours_gold, early_securing_hours_diamond, retired_therapist_code_grace_days, cash_handover_proximity_enabled, cash_handover_range_m, hub_cash_proximity_enabled, hub_cash_range_m, cash_before_pickup_required, updated_at";

export function useDistanceBands() {
  return useQuery({
    queryKey: ["operating-settings", "distance-bands"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("distance_surcharge_bands")
        .select(BAND_COLUMNS)
        .order("min_km", { ascending: true });
      if (error) throw new Error(error.message);
      return z.array(bandSchema).parse(data ?? []);
    },
    staleTime: 60_000,
  });
}

export function useOperatingSettings() {
  return useQuery({
    queryKey: ["operating-settings", "single"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("operating_settings")
        .select(SETTINGS_COLUMNS)
        .eq("id", 1);
      if (error) throw new Error(error.message);
      const rows = z.array(operatingSettingsSchema).parse(data ?? []);
      return rows[0] ?? null;
    },
    staleTime: 60_000,
  });
}

/**
 * The settings row is a single row by constraint, so every patch targets id 1.
 * A hub manager reaching this mutation gets a Postgres permission error rather
 * than a silent no-op — the policy grants write to superadmin only.
 */
export function useUpdateOperatingSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (patch: Partial<Omit<OperatingSettings, "id" | "updated_at">>) => {
      const { error } = await supabase.from("operating_settings").update(patch).eq("id", 1);
      if (error) throw new Error(error.message);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["operating-settings"] }),
  });
}

export type DistanceBandPatch = Partial<Omit<DistanceBand, "id">>;

export function useUpdateDistanceBands() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (edits: readonly { id: string; patch: DistanceBandPatch }[]) => {
      // Sequential on purpose: the bands are five rows and the failure message
      // should name the band that was refused, not the last one to settle.
      for (const edit of edits) {
        const { error } = await supabase
          .from("distance_surcharge_bands")
          .update(edit.patch)
          .eq("id", edit.id);
        if (error) throw new Error(error.message);
      }
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["operating-settings"] }),
  });
}
